/**
 * Tournament awards: MVP and anything else a director hands out.
 *
 * ## Why this is a shortlist and not a formula
 *
 * There is no MVP specification anywhere. Spirit has one (WFDF publishes the
 * sheet, the scale and the ranking rule); MVP does not, and the data here
 * could not support one if it did: credit is unevenly collected, defence is
 * untracked, and nobody records who was on the field. A weighting invented in
 * this file would manufacture an authority that gets argued with in front of
 * an audience at the closing ceremony.
 *
 * So an award is a SHORTLIST, a note per candidate, and a recorded decision.
 * `shared/player-stats.ts` supplies the evidence; a human supplies the
 * judgment; this module remembers what they decided and who may see it.
 *
 * ## Why it is not a tournament setting
 *
 * The settings row is served publicly (`shared/settings.ts`). A shortlist with
 * notes read off it before the ceremony is exactly the campaigning problem
 * that made spirit standings default to private, so awards live in their own
 * gated storage key and reach the public through `publicAwards` alone.
 */

import type { Identity } from "./identity";
import type { PlayerStatRow } from "./player-stats";

export const MAX_AWARD_TITLE = 40;
export const MAX_AWARD_NOTE = 400;
export const MAX_AWARD_CANDIDATES = 20;

export interface AwardCandidate {
  /** A `Player.id` from some team's roster. Not resolved here: the caller
   *  already holds the roster and the stats rows. */
  playerId: string;
  /** Why they are on the list. The director's own words, never generated. */
  note?: string;
}

export interface Award {
  id: string;
  /** The division this is awarded in. `UNASSIGNED_DIVISION` is a legitimate
   *  bucket, and a division deleted later leaves the id dangling rather than
   *  silently rehoming somebody's prize. */
  divisionId: string;
  /** "MVP", "Best newcomer", "Most improved": generic on purpose. */
  title: string;
  candidates: AwardCandidate[];
  /** The winner. Absent until the director decides. */
  decision?: { playerId: string; note?: string };
  /**
   * When it was announced. Absent = still private, whatever the master switch
   * says: both must be true for anybody outside the admin to see a winner.
   * Per-award because that is the act a director actually performs (Open at
   * 17:00, Women's at 17:05), while the setting is the one tap that takes them
   * all back down if something is wrong.
   */
  announcedAt?: number;
  updatedAt: number;
}

/**
 * What the board and the overlay may see.
 *
 * Structural, not a filter: there is no field here for a note or for the
 * shortlist, so a candidate list cannot reach a spectator by being forgotten
 * in one branch. Same shape of protection as `SpiritScoreRecord`.
 */
export interface PublicAward {
  id: string;
  divisionId: string;
  title: string;
  playerId: string;
  announcedAt: number;
}

/** A winner's evidence belongs to the awarded division, even when their
 * roster identity is merged with entries from other divisions. Missing
 * records stay missing; the ceremony must never manufacture zero totals. */
export function awardPlayerStats(
  award: PublicAward,
  players: readonly PlayerStatRow[],
  identity?: Identity,
): PlayerStatRow | undefined {
  const id = identity?.idOf(award.playerId) ?? award.playerId;
  return players.find((row) => row.playerId === id && row.divisionId === award.divisionId);
}

/**
 * The announced awards, or nothing.
 *
 * Both gates are checked here, in one place, for the same reason the spirit
 * projection lives inside the Durable Object: a spectator has no credential,
 * so this decision is the only thing between a private shortlist and the open
 * web.
 */
export function publicAwards(awards: readonly Award[], awardsPublic: boolean): PublicAward[] {
  if (!awardsPublic) return [];
  const out: PublicAward[] = [];
  for (const award of awards) {
    if (award.announcedAt === undefined || award.decision === undefined) continue;
    out.push({
      id: award.id,
      divisionId: award.divisionId,
      title: award.title,
      playerId: award.decision.playerId,
      announcedAt: award.announcedAt,
    });
  }
  return out.sort((a, b) => a.announcedAt - b.announcedAt);
}

/** Storage key. One row per award. */
export function awardKey(id: string): string {
  return `award:${id}`;
}

/** Awards in a division, most recently touched last so the list is stable. */
export function awardsIn(awards: readonly Award[], divisionId: string): Award[] {
  return awards
    .filter((a) => a.divisionId === divisionId)
    .sort((a, b) => a.title.localeCompare(b.title) || a.id.localeCompare(b.id));
}

/** Plain-English state for one award, so the UI never re-derives it. */
export type AwardState = "empty" | "shortlisted" | "decided" | "announced";

export function awardState(award: Award): AwardState {
  if (award.announcedAt !== undefined && award.decision !== undefined) return "announced";
  if (award.decision !== undefined) return "decided";
  return award.candidates.length > 0 ? "shortlisted" : "empty";
}

/**
 * Whether the decision names somebody who is not on the shortlist.
 *
 * Allowed (a director may pick whoever they like, and a shortlist is a
 * thinking tool, not a ballot), but worth saying out loud on the screen where
 * the two are side by side.
 */
export function decidedOffList(award: Award): boolean {
  if (award.decision === undefined) return false;
  return !award.candidates.some((c) => c.playerId === award.decision?.playerId);
}
